import React, { useEffect, useRef, useState } from "react";
import { Play, X, MessageCircle, Phone, MessageSquareText } from "lucide-react";
import { api, links } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { Container, Reveal, SectionHead } from "../site/Reveal";
import { trackVideo } from "../../lib/videoTrack";

const PlayerActions = ({ v, onClose }) => {
  const { content, openChat, t } = useSite();
  const l = links(content);
  const pill = "inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-[14px] font-semibold transition-colors";
  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
      <button onClick={() => { trackVideo(v.n, "chat"); if (!localStorage.getItem("sw_chat_sid")) localStorage.setItem("sw_chat_video", v.title); onClose(); openChat(); }} className={`${pill} bg-[#0e8c7f] text-white hover:bg-[#0b6f65]`} data-testid="watch-player-chat"><MessageCircle size={16} />{t("Chat with us")}</button>
      <span className="inline-flex overflow-hidden rounded-full border border-white/40 text-white" data-testid="watch-player-calltext">
        <a href={l.tel} onClick={() => trackVideo(v.n, "call")} className={`${pill} rounded-none hover:bg-white/15`} data-testid="watch-player-call"><Phone size={15} />{t("Call")}</a>
        <span className="w-px bg-white/30" />
        <a href={l.sms} onClick={() => trackVideo(v.n, "text")} className={`${pill} rounded-none hover:bg-white/15`} data-testid="watch-player-text"><MessageSquareText size={15} />{t("Text")}</a>
      </span>
    </div>
  );
};

const Player = ({ v, onClose }) => {
  const played = useRef(false);
  const onPlay = () => { if (!played.current) { played.current = true; trackVideo(v.n, "play"); } };
  return (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={onClose} data-testid="watch-player">
    <div className="relative w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25" aria-label="Close" data-testid="watch-player-close"><X size={18} /></button>
      <video src={v.src} poster={v.poster} controls autoPlay playsInline onPlay={onPlay} onEnded={() => trackVideo(v.n, "complete")} className="mx-auto aspect-[9/16] max-h-[66vh] w-auto max-w-full rounded-2xl bg-black" data-testid="watch-player-video" />
      <p className="mt-3 text-center text-[15px] font-semibold text-white">{v.title}</p>
      <PlayerActions v={v} onClose={onClose} />
    </div>
  </div>
  );
};

const WatchLearn = () => {
  const { t } = useSite();
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/videos").then((r) => setVideos(r.data)).catch(() => {}); }, []);
  if (!videos.length) return null;
  return (
    <section id="watch-learn" className="scroll-mt-28 bg-[#f4faf8] py-20 md:py-24" data-testid="watch-learn">
      <Container>
        <SectionHead eyebrow={t("Watch & Learn")} title={t("Short videos to help you ship with confidence")} text={t("Quick, friendly tips from our team — tap any video to watch.")} />
        <div className="-mx-5 mt-12 flex snap-x snap-mandatory scroll-pl-5 gap-5 overflow-x-auto px-5 pb-4 md:mx-0 md:grid md:grid-cols-4 md:overflow-visible md:px-0 lg:grid-cols-5">
          {videos.map((v, i) => (
            <Reveal key={v.n} delay={(i % 5) * 80} className="w-[62%] shrink-0 snap-start sm:w-[40%] md:w-auto">
              <button onClick={() => setOpen(v)} className="group block w-full text-left" data-testid={`watch-video-${v.n}`}>
                <div className="relative overflow-hidden rounded-2xl shadow-[0_10px_30px_-12px_rgba(15,46,42,0.35)]">
                  <img src={v.poster} alt={v.title} loading="lazy" className="aspect-[9/16] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/20">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-[#0e8c7f] shadow-lg transition-transform duration-300 group-hover:scale-110"><Play size={22} className="ml-1" fill="currentColor" /></span>
                  </span>
                </div>
                <p className="mt-3 text-[15px] font-semibold leading-snug text-[#0f2e2a]">{v.title}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </Container>
      {open && <Player v={open} onClose={() => setOpen(null)} />}
    </section>
  );
};

export default WatchLearn;
